import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'glowp_cookie_consent_v1';
const CONSENT_VERSION = 1;
const CONSENT_DURATION_MS = 13 * 30 * 24 * 60 * 60 * 1000; // 13 mois (recommandation CNIL)

export interface CookieConsent {
  version: number;
  timestamp: string;
  essential: true;
  analytics: boolean;
  marketing: boolean;
}

const ACCEPT_ALL = {
  version: CONSENT_VERSION,
  essential: true as const,
  analytics: true,
  marketing: true,
};

const REFUSE_ALL = {
  version: CONSENT_VERSION,
  essential: true as const,
  analytics: false,
  marketing: false,
};

function readConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;

    const ageMs = Date.now() - new Date(parsed.timestamp).getTime();
    if (ageMs > CONSENT_DURATION_MS) return null;

    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(
  consent: Omit<CookieConsent, 'timestamp'>
): CookieConsent {
  const stored: CookieConsent = {
    ...consent,
    timestamp: new Date().toISOString(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  return stored;
}

// Renommé depuis useCookieConsent : le mot 'cookie/consent' dans les paths
// est filtré par les AdBlocks agressifs (uBlock, AdGuard, Brave Shields).
export function useNoticePreferences() {
  const [consent, setConsent] = useState<CookieConsent | null>(() =>
    readConsent()
  );
  const [showSettings, setShowSettings] = useState(false);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setConsent(readConsent());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const hasConsent = consent !== null;

  const acceptAll = useCallback(() => {
    setConsent(writeConsent(ACCEPT_ALL));
    setShowSettings(false);
  }, []);

  const refuseAll = useCallback(() => {
    setConsent(writeConsent(REFUSE_ALL));
    setShowSettings(false);
  }, []);

  const saveCustom = useCallback((analytics: boolean, marketing: boolean) => {
    setConsent(
      writeConsent({
        version: CONSENT_VERSION,
        essential: true,
        analytics,
        marketing,
      })
    );
    setShowSettings(false);
  }, []);

  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);

  return {
    consent,
    hasConsent,
    showSettings,
    acceptAll,
    refuseAll,
    saveCustom,
    openSettings,
    closeSettings,
  };
}
